import { useState, useEffect, useCallback, useRef } from 'react';
import {
  listTimelineClips,
  listSceneVersions,
  listSubtitles,
  listExports,
  createExport,
  deleteExport,
  createCalendarEvent,
  cuesToSrt,
  renderDimensions,
  uploadBlobToStorage,
  downloadMedia,
  formatDuration,
  PLATFORMS,
  type ContentProject,
  type ContentScene,
  type ContentClip,
  type ContentSubtitle,
  type ContentExport,
  type CalendarStatus,
} from '@/lib/contentStudio';
import { assembleScenes } from '@/lib/videoAssembly';
import { useAuth } from '@/hooks/useAuth';

interface Props {
  project: ContentProject;
  scenes: ContentScene[];
}

function shortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Assemblage final : concatène toutes les vidéos des scènes (dans l'ordre de la
 * timeline) en UN seul MP4, incruste éventuellement les sous-titres, puis
 * permet de le télécharger et de le sauvegarder durablement dans le projet.
 */
export default function FinalAssemblyPanel({ project, scenes }: Props) {
  const { user } = useAuth();
  const [clips, setClips] = useState<ContentClip[]>([]);
  const [subs, setSubs] = useState<ContentSubtitle[]>([]);
  const [exports, setExports] = useState<ContentExport[]>([]);
  const [videoMap, setVideoMap] = useState<Record<number, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [assembling, setAssembling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [subtitleId, setSubtitleId] = useState<string>('');
  const [result, setResult] = useState<{ blob: Blob; objectUrl: string; subtitlesBurned: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<{ url: string; id: number | null } | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  // Publication au calendrier de contenu (rattachement du MP4 à un événement planifié).
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedulePlatform, setSchedulePlatform] = useState(PLATFORMS[0]);
  const [scheduleDate, setScheduleDate] = useState(todayKey());
  const [scheduleStatus, setScheduleStatus] = useState<CalendarStatus>('planned');
  const [scheduleTitle, setScheduleTitle] = useState('');
  const [scheduleExport, setScheduleExport] = useState<{ url: string; id: number | null } | null>(null);
  const [scheduling, setScheduling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cl, su, ex] = await Promise.all([
        listTimelineClips(project.id),
        listSubtitles(project.id),
        listExports(project.id),
      ]);
      setClips(cl);
      setSubs(su);
      setExports(ex);
      const map: Record<number, string | null> = {};
      for (const s of scenes) {
        // eslint-disable-next-line no-await-in-loop
        const versions = await listSceneVersions(s.id);
        const active = versions.find((v) => v.id === s.active_version_id)
          || versions.find((v) => v.video_url)
          || versions[0];
        map[s.id] = active?.video_url || null;
      }
      setVideoMap(map);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [project.id, scenes]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  // Ordre des scènes : celui de la timeline si elle existe, sinon l'ordre des scènes.
  const orderedSceneIds: number[] = clips.length > 0
    ? clips.map((c) => c.scene_id).filter((v): v is number => v != null)
    : scenes.map((s) => s.id);

  const readyClips = orderedSceneIds
    .map((id) => ({ scene: scenes.find((s) => s.id === id) || null, url: videoMap[id] }))
    .filter((c) => c.scene && c.url) as { scene: ContentScene; url: string }[];

  const missing = orderedSceneIds.length - readyClips.length;
  const totalDuration = readyClips.reduce((acc, c) => acc + (c.scene.duration || 0), 0);
  const dims = renderDimensions(project.aspect_ratio);

  const handleAssemble = async () => {
    if (assembling || readyClips.length === 0) return;
    setAssembling(true);
    setError(null);
    setInfo(null);
    setProgress(0);
    try {
      const chosen = subs.find((s) => String(s.id) === subtitleId) || null;
      const srt = chosen ? cuesToSrt(chosen.cues) : null;
      const { blob, subtitlesBurned } = await assembleScenes({
        urls: readyClips.map((c) => c.url),
        width: dims.width,
        height: dims.height,
        fps: 24,
        srt,
        onProgress: setProgress,
      });
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;
      setResult({ blob, objectUrl, subtitlesBurned });
      setInfo(
        subtitlesBurned
          ? 'Vidéo finale assemblée avec sous-titres incrustés.'
          : `Vidéo finale assemblée (${readyClips.length} scène(s)).`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "L'assemblage a échoué");
    } finally {
      setAssembling(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    downloadMedia(result.objectUrl, `video-finale-${project.id}.mp4`);
  };

  const handleSave = async () => {
    if (!result) return;
    setSaving(true);
    setError(null);
    try {
      const url = await uploadBlobToStorage(result.blob, `video-finale-projet-${project.id}-${Date.now()}.mp4`, 'content-studio/exports');
      const row = await createExport(project.id, {
        format: 'mp4',
        resolution: `${dims.width}x${dims.height}`,
        url,
        status: 'ready',
        options: { scenes: readyClips.length, subtitles: result.subtitlesBurned },
      });
      if (row) setExports((prev) => [row, ...prev]);
      setLastSaved({ url, id: row?.id ?? null });
      setInfo('Vidéo finale sauvegardée dans le projet (lien permanent). Vous pouvez maintenant la publier au calendrier.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExport = async (id: number) => {
    try {
      await deleteExport(id);
      setExports((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const openSchedule = (url: string, exportId: number | null) => {
    setScheduleExport({ url, id: exportId });
    setScheduleTitle(`${project.name} — ${PLATFORMS[0]}`);
    setScheduleDate(todayKey());
    setScheduleStatus('planned');
    setSchedulePlatform(PLATFORMS[0]);
    setScheduleOpen(true);
  };

  const handleSchedule = async () => {
    if (!user?.id || !scheduleExport || scheduling) return;
    setScheduling(true);
    setError(null);
    try {
      await createCalendarEvent(user.id, {
        project_id: project.id,
        export_id: scheduleExport.id,
        media_url: scheduleExport.url,
        media_kind: 'video',
        thumbnail_url: project.thumbnail_url,
        title: (scheduleTitle.trim() || `${project.name} — publication`).slice(0, 140),
        platform: schedulePlatform,
        scheduled_date: scheduleDate,
        status: scheduleStatus,
        notes: `Export MP4 assemblé (${readyClips.length} scène(s)).`,
      });
      setScheduleOpen(false);
      setInfo(`Publication planifiée sur ${schedulePlatform} le ${scheduleDate}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Planification impossible');
    } finally {
      setScheduling(false);
    }
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-movie-2-fill text-accent-500"></i>Assemblage final
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            Concatène les vidéos de chaque scène en un seul MP4 (ordre du montage) et incruste les sous-titres si tu le souhaites.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}
      {info && !error && (
        <div className="mb-3 flex items-center gap-2 text-sm text-accent-700 bg-accent-50 px-3 py-2 rounded-lg">
          <i className="ri-checkbox-circle-line"></i>{info}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-background-100 rounded-lg px-3 py-2">
          <p className="text-[11px] text-foreground-500">Scènes prêtes</p>
          <p className="text-sm font-bold text-foreground-900">{readyClips.length}/{orderedSceneIds.length}</p>
        </div>
        <div className="bg-background-100 rounded-lg px-3 py-2">
          <p className="text-[11px] text-foreground-500">Durée estimée</p>
          <p className="text-sm font-bold text-foreground-900">{formatDuration(totalDuration)}</p>
        </div>
        <div className="bg-background-100 rounded-lg px-3 py-2">
          <p className="text-[11px] text-foreground-500">Format</p>
          <p className="text-sm font-bold text-foreground-900">{dims.width}×{dims.height}</p>
        </div>
        <div className="bg-background-100 rounded-lg px-3 py-2">
          <p className="text-[11px] text-foreground-500">Ordre</p>
          <p className="text-sm font-bold text-foreground-900">{clips.length > 0 ? 'Timeline' : 'Scènes'}</p>
        </div>
      </div>

      {missing > 0 && (
        <p className="mb-3 text-[11px] text-foreground-500 flex items-center gap-1.5">
          <i className="ri-information-line text-primary-500"></i>
          {missing} scène(s) sans vidéo générée seront ignorées à l&apos;assemblage.
        </p>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="min-w-[200px]">
          <label className="block text-xs font-medium text-foreground-600 mb-1.5">Sous-titres à incruster</label>
          <div className="relative">
            <select
              value={subtitleId}
              onChange={(e) => setSubtitleId(e.target.value)}
              className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
            >
              <option value="">Aucun sous-titre</option>
              {subs.map((s) => (
                <option key={s.id} value={s.id}>{s.cues.length} répliques · {s.format}</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
          </div>
        </div>
        <button
          type="button"
          onClick={handleAssemble}
          disabled={assembling || readyClips.length === 0 || loading}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {assembling ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-magic-line"></i>}
          {assembling ? 'Assemblage…' : 'Assembler la vidéo finale'}
        </button>
      </div>

      {assembling && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-[11px] text-foreground-500 mb-1">
            <span>Assemblage des scènes…</span>
            <span>{Math.round(progress * 100)}%</span>
          </div>
          <div className="h-2 rounded-full bg-background-100 overflow-hidden">
            <div className="h-full bg-accent-500 transition-all" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        </div>
      )}

      {result && (
        <div className="mb-4 bg-background-100 rounded-xl p-3">
          <video src={result.objectUrl} controls playsInline className="w-full max-h-72 rounded-lg bg-black object-contain" />
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button
              type="button"
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-download-2-line"></i>Télécharger le MP4
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-background-200/70 text-sm font-medium text-foreground-700 whitespace-nowrap hover:bg-background-200/70 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              Sauvegarder dans le projet
            </button>
            {lastSaved && (
              <button
                type="button"
                onClick={() => openSchedule(lastSaved.url, lastSaved.id)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-calendar-schedule-line"></i>Publier au calendrier
              </button>
            )}
          </div>
        </div>
      )}

      {exports.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-400 mb-2 flex items-center gap-2">
            <i className="ri-archive-line"></i>Exports sauvegardés
          </h4>
          <div className="space-y-1.5">
            {exports.map((ex) => (
              <div key={ex.id} className="flex items-center gap-2 bg-background-50 border border-background-200/70 rounded-lg px-3 py-2">
                <i className="ri-file-video-line text-foreground-400"></i>
                <span className="text-xs text-foreground-700 flex-1 truncate">
                  MP4 · {ex.resolution || '—'} · {shortDate(ex.created_at)}
                </span>
                {ex.url && (
                  <button
                    type="button"
                    onClick={() => openSchedule(ex.url as string, ex.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                    title="Publier au calendrier"
                  >
                    <i className="ri-calendar-schedule-line"></i>
                  </button>
                )}
                {ex.url && (
                  <button
                    type="button"
                    onClick={() => downloadMedia(ex.url as string, `video-finale-${project.id}.mp4`)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                    title="Télécharger"
                  >
                    <i className="ri-download-2-line"></i>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDeleteExport(ex.id)}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  title="Supprimer"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {scheduleOpen && scheduleExport && (
        <div className="mt-4 rounded-xl border border-primary-200/70 bg-primary-50/40 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-primary-700 flex items-center gap-2">
              <i className="ri-calendar-schedule-line"></i>Publier au calendrier
            </h4>
            <button
              type="button"
              onClick={() => setScheduleOpen(false)}
              className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer"
              title="Fermer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre de la publication</label>
              <input
                value={scheduleTitle}
                onChange={(e) => setScheduleTitle(e.target.value)}
                className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Plateforme</label>
              <div className="relative">
                <select
                  value={schedulePlatform}
                  onChange={(e) => setSchedulePlatform(e.target.value)}
                  className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Date planifiée</label>
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Statut</label>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
                {([['planned', 'Prévu'], ['in_progress', 'En cours'], ['published', 'Publié']] as const).map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setScheduleStatus(val as CalendarStatus)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${scheduleStatus === val ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSchedule}
            disabled={scheduling}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {scheduling ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
            {scheduling ? 'Planification…' : 'Planifier la publication'}
          </button>
        </div>
      )}
    </div>
  );
}